import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import TargetingOptions from './TargetingOptions';
import { COLORS } from '../../constants/colors';
import './BlastsCSS/DialogStyles.css';

// Configure dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

const ScheduleBlastDialog = ({ open, onClose, onConfirm, blast }) => {
  const [scheduledTime, setScheduledTime] = useState(null);
  const [timeOfDay, setTimeOfDay] = useState('evening'); // Default to evening
  const [showTargeting, setShowTargeting] = useState(false);

  // Targeting state
  const [selectedGenders, setSelectedGenders] = useState(['all']);
  const [selectedAgeRange, setSelectedAgeRange] = useState('all');
  const [membershipFilter, setMembershipFilter] = useState('all');

  const handleConfirm = async () => {
    if (scheduledTime) {
      // Create a new Date object with the selected date and set the time based on the selected time of day
      /* const hour = timeOfDay === 'morning' ? 10 : timeOfDay === 'afternoon' ? 15 : timeOfDay === 'evening' ? 20 : 0;
      const scheduledDate = dayjs(scheduledTime)
        .tz('America/New_York')
        .set('hour', hour)
        .set('minute', 0)
        .set('second', 0)
        .set('millisecond', 0)
        .toISOString(); */

      // Get the selected date components in local timezone
      const year = scheduledTime.year();
      const month = scheduledTime.month();
      const date = scheduledTime.date();

      // Set the time based on time of day (in EST)
      const hour = timeOfDay === 'morning' ? 10 : timeOfDay === 'afternoon' ? 15 : 20;
      const minute = 0;

      // Create a new date in EST with the same date components
      const scheduledDate = dayjs.tz(
        new Date(year, month, date, hour, minute, 0, 0),
        "America/New_York"
      ).toISOString();

      // Include targeting options in the confirmation if targeting is enabled
      const targetingOptions = showTargeting ? {
        selectedGenders,
        selectedAgeRange,
        membershipStatus: membershipFilter
      } : null;

      // Call onConfirm with the scheduled date
      onConfirm(scheduledDate, targetingOptions);
      onClose();
    } else {
      alert('Please select a valid date.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        className: 'dialog-container'
      }}
    >
      <DialogTitle>
        <Typography 
          component="div"
          className="dialog-title inter-custom"
        >
          Schedule Blast
        </Typography>
      </DialogTitle>
      <DialogContent className="dialog-content">
        {blast ? (
          <Box display="flex" flexDirection="column" alignItems="center" sx={{ mt: 2 }}>
            <Typography className="dialog-message dialog-message-first">
              Blast: {blast.message}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Date"
                value={scheduledTime}
                onChange={setScheduledTime}
                disablePast={true}
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    variant: "outlined",
                    className: "dialog-input",
                    helperText: dayjs(scheduledTime).isBefore(dayjs(), 'day') 
                      ? "Please select a future date" 
                      : "Pick a date for the blast"
                  }
                }}
              />
            </LocalizationProvider>
            <FormControl fullWidth margin="normal">
              <InputLabel className="dialog-input-label">Time of Day (EST)</InputLabel>
              <Select
                value={timeOfDay}
                onChange={(e) => setTimeOfDay(e.target.value)}
                label="Time of Day (EST)"
                className="dialog-input"
              >
                <MenuItem value="morning">Morning (10:00 AM)</MenuItem>
                <MenuItem value="afternoon">Afternoon (3:00 PM)</MenuItem>
                <MenuItem value="evening">Evening (8:00 PM)</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={showTargeting}
                  onChange={(e) => setShowTargeting(e.target.checked)}
                  className="demographics-checkbox"
                />
              }
              label="Add demographics targeting"
              sx={{ mt: 2, alignSelf: 'flex-start' }}
            />

            {showTargeting && (
              <Box sx={{ width: '100%' }}>
                <TargetingOptions
                  selectedGenders={selectedGenders}
                  setSelectedGenders={setSelectedGenders}
                  selectedAgeRange={selectedAgeRange}
                  setSelectedAgeRange={setSelectedAgeRange}
                  membershipFilter={membershipFilter}
                  setMembershipFilter={setMembershipFilter}
                  includeMembershipQuestion={true}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Typography className="dialog-message">
            No blast selected.
          </Typography>
        )}
      </DialogContent>
      <DialogActions className="dialog-actions">
        <Button 
          onClick={onClose} 
          className="dialog-button dialog-button-secondary inter-custom"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          className="dialog-button dialog-button-primary inter-custom"
          disabled={!blast}
        >
          Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleBlastDialog;